import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Divider,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { useAlert } from '../core/AlertContext';
import { IKurtaxeRate } from '../Interfaces/IReservationPrice';
import { downloadKurtaxeExport, useGetKurtaxeRates, useUpdateKurtaxeRates } from '../hooks/kurtaxe';
import { fieldSx, primaryButtonSx } from '../core/pvtaStyles';

const RATE_ORDER: Array<{ category: IKurtaxeRate['category']; label: string }> = [
    { category: 'EW', label: 'Erwachsene (18+)' },
    { category: 'AGE_7_17', label: '7-17 Jahre' },
    { category: 'AGE_0_6', label: '0-6 Jahre' },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, index) => CURRENT_YEAR - index);

export const KurtaxePanel: React.FC = () => {
    const { kurtaxeRates, isLoadingKurtaxeRates } = useGetKurtaxeRates();
    const { updateKurtaxeRates, isUpdatingKurtaxeRates } = useUpdateKurtaxeRates();
    const { showAlert } = useAlert();

    const [rateValues, setRateValues] = useState<Record<string, string>>({});
    const [year, setYear] = useState<number>(CURRENT_YEAR);
    const [isDownloading, setIsDownloading] = useState(false);

    const ratesByCategory = useMemo(() => {
        const map: Record<string, number> = {};
        kurtaxeRates.forEach((rate) => {
            map[rate.category] = rate.rate;
        });
        return map;
    }, [kurtaxeRates]);

    useEffect(() => {
        setRateValues(
            RATE_ORDER.reduce<Record<string, string>>((acc, { category }) => {
                acc[category] = String(ratesByCategory[category] ?? 0);
                return acc;
            }, {}),
        );
    }, [ratesByCategory]);

    const handleSaveRates = async () => {
        try {
            const payload = RATE_ORDER.map(({ category }) => ({
                category,
                rate: Number(rateValues[category] ?? 0),
            }));
            await updateKurtaxeRates(payload);
            showAlert({ text: 'Kurtaxe-Sätze gespeichert.' });
        } catch {
            showAlert({ text: 'Fehler beim Speichern der Kurtaxe-Sätze.', severity: 'error' });
        }
    };

    const handleExport = async (fmt: 'pdf' | 'csv') => {
        setIsDownloading(true);
        try {
            await downloadKurtaxeExport(year, fmt);
            showAlert({ text: `Kurtaxe-Export ${year} (${fmt.toUpperCase()}) erstellt.` });
        } catch {
            showAlert({ text: 'Fehler beim Erstellen des Kurtaxe-Exports.', severity: 'error' });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Kurtaxe (Stadt Furtwangen)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Sätze pro Person und Übernachtung. Die Zuordnung der Kategorien zu den Altersstufen
                    wird je Übernachtungskosten-Eintrag gepflegt.
                </Typography>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'flex-end' }}
                    sx={{ mb: 1 }}
                >
                    {RATE_ORDER.map(({ category, label }) => (
                        <TextField
                            key={category}
                            label={label}
                            type="number"
                            size="small"
                            inputProps={{ min: 0, step: 0.01 }}
                            value={rateValues[category] ?? ''}
                            onChange={(event) =>
                                setRateValues((prev) => ({ ...prev, [category]: event.target.value }))
                            }
                            disabled={isLoadingKurtaxeRates}
                            InputProps={{ endAdornment: <Typography variant="caption">€</Typography> }}
                            sx={{ ...fieldSx, width: { xs: '100%', sm: 130 } }}
                        />
                    ))}
                    <Button
                        onClick={() => { void handleSaveRates(); }}
                        disabled={isUpdatingKurtaxeRates || isLoadingKurtaxeRates}
                        sx={{ ...primaryButtonSx, whiteSpace: 'nowrap' }}
                    >
                        Sätze speichern
                    </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                    Jahres-Export für die Stadt
                </Typography>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                    <TextField
                        label="Jahr"
                        select
                        size="small"
                        value={year}
                        onChange={(event) => setYear(Number(event.target.value))}
                        sx={{ ...fieldSx, minWidth: 120 }}
                    >
                        {YEAR_OPTIONS.map((option) => (
                            <MenuItem key={option} value={option}>
                                {option}
                            </MenuItem>
                        ))}
                    </TextField>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => { void handleExport('pdf'); }}
                            disabled={isDownloading}
                        >
                            PDF
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadIcon />}
                            onClick={() => { void handleExport('csv'); }}
                            disabled={isDownloading}
                        >
                            CSV
                        </Button>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );
};

export default KurtaxePanel;
